/**
 * Dictation Permissions (macOS)
 * Uses native Tauri IPC commands for Accessibility permission.
 * Note: Input Monitoring is NOT required for Fn key detection.
 */

import { invoke } from '@tauri-apps/api/core';

import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';

export type PermissionState = 'granted' | 'denied' | 'unknown';

export interface DictationPermissions {
  accessibility: PermissionState;
}

/** Check if Accessibility permission is granted. */
export async function checkAccessibilityPermission(): Promise<PermissionState> {
  if (!isTauri()) {
    logger.debug({
      event_type: 'PERMISSION_CHECK_SKIPPED_NOT_TAURI',
      details: { permission: 'accessibility' },
    });
    return 'granted';
  }

  try {
    logger.info({ event_type: 'PERMISSION_CHECK_ACCESSIBILITY_START' });
    const granted = await invoke<boolean>('check_accessibility_permission');
    logger.info({
      event_type: 'PERMISSION_CHECK_ACCESSIBILITY_RESULT',
      details: { granted },
    });
    return granted ? 'granted' : 'denied';
  } catch (error: unknown) {
    logger.error({
      event_type: 'PERMISSION_CHECK_ACCESSIBILITY_ERROR',
      details: { error },
    });
    return 'unknown';
  }
}

/**
 * Request Accessibility permission.
 * - First call after install: shows the macOS permission dialog.
 * - Subsequent calls when not trusted: opens System Settings → Privacy → Accessibility.
 */
export async function requestAccessibilityPermission(): Promise<PermissionState> {
  if (!isTauri()) {
    logger.debug({
      event_type: 'PERMISSION_REQUEST_SKIPPED_NOT_TAURI',
      details: { permission: 'accessibility' },
    });
    return 'granted';
  }

  try {
    logger.info({ event_type: 'PERMISSION_REQUEST_ACCESSIBILITY_START' });
    const granted = await invoke<boolean>('request_accessibility_permission');
    logger.info({
      event_type: 'PERMISSION_REQUEST_ACCESSIBILITY_RESULT',
      details: { granted },
    });
    return granted ? 'granted' : 'denied';
  } catch (error: unknown) {
    logger.error({
      event_type: 'PERMISSION_REQUEST_ACCESSIBILITY_ERROR',
      details: { error },
    });
    return 'unknown';
  }
}

/**
 * Open System Settings → Privacy → Accessibility directly, without prompting.
 * Used by the revoked-permission recovery toast: a previously-granted bundle
 * never re-surfaces the AX dialog, so `requestAccessibilityPermission`'s
 * first-attempt prompt path is a no-op and Settings is the only useful action.
 */
export async function openAccessibilityPrivacySettings(): Promise<void> {
  if (!isTauri()) {
    return;
  }
  try {
    await invoke('open_accessibility_privacy_settings');
  } catch (error: unknown) {
    logger.error({
      event_type: 'PERMISSION_OPEN_ACCESSIBILITY_SETTINGS_ERROR',
      details: { error },
    });
  }
}

/** Check dictation permissions (Accessibility only). */
export async function checkDictationPermissions(): Promise<DictationPermissions> {
  logger.info({ event_type: 'PERMISSION_CHECK_DICTATION_START' });

  const accessibility = await checkAccessibilityPermission();
  const result = { accessibility };

  logger.info({
    event_type: 'PERMISSION_CHECK_DICTATION_RESULT',
    details: result,
  });

  return result;
}
